import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle, CheckCircle, Loader2, ShoppingCart, Truck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { HerbReviews } from "../components/HerbReviews";
import { setPageMeta } from "../lib/seo";
import { trackProductViewed } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HerbDetail = () => {
  const { herbId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [herb, setHerb] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHerbDetail = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/herbs/${herbId}`);
      setHerb(response.data);
      trackProductViewed(response.data);
      const h = response.data;
      setPageMeta(
        `${h.name} (${h.scientific_name || "Medicinal Herb"}) — Benefits, Uses & Safety`,
        (h.description || `Learn about ${h.name}: benefits, how to use, dosage and safety precautions.`).slice(0, 155)
      );
    } catch (error) {
      console.error('Error fetching herb detail:', error);
      toast.error("Failed to load herb details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [herbId]);

  useEffect(() => {
    fetchHerbDetail();
  }, [fetchHerbDetail]);

  const handleAddToCart = () => {
    addItem(herb.id);
    toast.success(`${herb.name} added to cart`);
  };

  const handleShopAll = () => navigate("/store");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!herb) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Herb not found</p>
          <Link to="/herbs">
            <Button>Back to Encyclopedia</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Link to="/herbs" className="inline-flex items-center text-primary hover:text-primary/80 mb-8" data-testid="back-to-encyclopedia">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Encyclopedia
        </Link>

        {/* Hero Image */}
        <div className="rounded-xl overflow-hidden mb-8 aspect-video bg-gray-100">
          <img
            src={herb.image_url}
            alt={herb.name}
            className="w-full h-full object-cover"
            data-testid="herb-detail-image"
          />
        </div>

        {/* Header with Buy Button */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-secondary text-primary text-sm font-medium rounded-full" data-testid="herb-category">
                  {herb.category}
                </span>
                <p className="text-[11px] text-gray-400 mt-2" data-testid="herb-educational-note">
                  Educational information describing traditional uses only — not medical advice or treatment claims.
                </p>
              </div>
              <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-2" data-testid="herb-name">
                {herb.name}
              </h1>
              <p className="font-mono text-gray-500" data-testid="herb-scientific-name">
                {herb.scientific_name}
              </p>
            </div>
            
            {/* Add to Cart Button */}
            <div className="flex-shrink-0 text-right">
              {herb.price_gbp && (
                <p className="text-2xl font-bold text-primary mb-2" data-testid="herb-detail-price">£{herb.price_gbp.toFixed(2)}</p>
              )}
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="h-14 px-8 rounded-full bg-accent hover:bg-accent/90 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="buy-now-btn"
              >
                <ShoppingCart className="mr-2 w-5 h-5" />
                Add to Cart
              </Button>
              <p className="text-xs text-gray-500 mt-2 flex items-center justify-end gap-1">
                <Truck className="w-3.5 h-3.5" /> Typically ships in 3–7 working days
              </p>
              {herb.amazon_url && (
                <a href={herb.amazon_url} target="_blank" rel="noopener noreferrer"
                   data-testid="herb-detail-amazon-link"
                   className="text-xs text-gray-400 hover:text-amber-600 mt-2 inline-flex items-center gap-1 transition-colors">
                  View on Amazon — external retailer <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mb-12">
          <p className="text-lg text-gray-700 leading-relaxed" data-testid="herb-description">
            {herb.long_description || herb.description}
          </p>
        </div>

        {/* Shop Callout */}
        <div className="mb-12 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-playfair text-xl font-bold text-gray-900 mb-2">
                Premium Quality {herb.name}
              </h3>
              <p className="text-gray-600">
                Freshly sourced, quality-checked and typically delivered to your door in 3–7 working days
              </p>
            </div>
            <Button
              onClick={handleAddToCart}
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-white rounded-full font-medium"
              data-testid="shop-category-btn"
            >
              <ShoppingCart className="mr-2 w-4 h-4" />
              Add to Cart
            </Button>
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-12">
          <h2 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
            Traditionally Associated Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {herb.benefits.map((benefit, index) => (
              <div
                key={`${benefit}-${index}`}
                className="flex items-start space-x-3 p-4 bg-secondary/50 rounded-lg"
                data-testid={`benefit-${index}`}
              >
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Uses */}
        <div className="mb-12">
          <h2 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
            Common Uses
          </h2>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <ul className="space-y-3">
              {herb.uses.map((use, index) => (
                <li key={`${use}-${index}`} className="flex items-start" data-testid={`use-${index}`}>
                  <span className="inline-block w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-700">{use}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Precautions */}
        <div className="mb-12">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-playfair text-xl font-bold text-amber-900 mb-2">
                  Precautions & Safety Information
                </h2>
                <p className="text-amber-800 leading-relaxed" data-testid="herb-precautions">
                  {herb.precautions}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <HerbReviews herbId={herb.id} herbName={herb.name} />

        {/* Bottom CTA */}
        <div className="bg-primary text-white rounded-xl p-8 text-center mb-12">
          <h2 className="font-playfair text-2xl md:text-3xl font-bold mb-3">
            Ready to Experience {herb.name}?
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Order premium quality {herb.name} from our Herb Store — every herb in stock, ships in 3–5 business days
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleAddToCart}
              size="lg"
              className="h-12 px-8 rounded-full bg-white text-primary hover:bg-gray-100 font-semibold"
              data-testid="buy-now-bottom-btn"
            >
              <ShoppingCart className="mr-2 w-5 h-5" />
              Add {herb.name} to Cart
            </Button>
            <Button
              onClick={handleShopAll}
              size="lg"
              variant="outline"
              className="h-12 px-8 rounded-full border-2 border-white text-white hover:bg-white/10 font-semibold"
              data-testid="shop-all-btn"
            >
              Shop All Herbs
            </Button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <p className="text-sm text-gray-600 leading-relaxed">
            <strong>Medical Disclaimer:</strong> This information is for educational purposes only and is not intended as medical advice. 
            Always consult with a qualified healthcare provider before using any herbal remedies, especially if you are pregnant, 
            nursing, taking medications, or have a medical condition.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HerbDetail;